// Angular Files
import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';

// Other External Files

// Teller Online Files
import { IErrorDto, PaymentIntegrationEnumDto } from 'apps/public-portal/src/app/core/api/PublicPortalApiClients';
import { DEFAULT_CONFIG_KEY } from 'apps/public-portal/src/app/core/services';

// Teller Online Library Files
import { TellerOnlineSiteMetadataService } from 'teller-online-libraries/core';

// Payment Integration Files
import { PaymentProcessorService, PaymentProcessorClass } from 'apps/public-portal/src/app/payment-integrations/base/interfaces';
import {
 PaymentProcessorConfig,
    PaymentMethodTypeEnum,
    PaymentMethodTypeEnumConvertor,
    PaymentMethodData
} from 'apps/public-portal/src/app/payment-integrations/base/models';
import {
    AggregatedPluginConfigDto,
    AvailablePaymentMethodsResponseDto,
    ConvenienceFeeResponseDto,
    SharedPaymentIntegrationApiClient,
    WalletCapabilityEntryDto
} from 'apps/public-portal/src/app/core/api/PaymentIntegrationApiClients';

// Deep-path imports bypass each integration's barrel (Vite barrel cycles — see
// TellerOnline.Frontend/CLAUDE.md). Each integration barrel exports its component
// before its service, and the component extends BasePaymentProcessorComponent —
// importing the barrel here creates a re-entrant cycle through Base.
import { MSBService } from 'apps/public-portal/src/app/payment-integrations/msb/service/msb.service';
import { GenericService } from 'apps/public-portal/src/app/payment-integrations/generic/service/generic.service';
import { DatacapService } from 'apps/public-portal/src/app/payment-integrations/datacap/service/datacap.service';
import { NicService } from 'apps/public-portal/src/app/payment-integrations/nic/service/nic.service';
import { DemoService } from 'apps/public-portal/src/app/payment-integrations/demo/service/demo.service';
import { CeleroService } from 'apps/public-portal/src/app/payment-integrations/celero/service/celero.service';
import { BridgePayService } from 'apps/public-portal/src/app/payment-integrations/bridge-pay/service/bridge-pay.service';
import { ElavonService } from 'apps/public-portal/src/app/payment-integrations/elavon/service/elavon.service';
import { PointAndPayService } from 'apps/public-portal/src/app/payment-integrations/point-and-pay/service/point-and-pay.service';
import { CyberSourceService } from 'apps/public-portal/src/app/payment-integrations/cyber-source/service/cyber-source.service';
import { WellsFargoService } from 'apps/public-portal/src/app/payment-integrations/wells-fargo/service/wells-fargo.service';
import { FisService } from 'apps/public-portal/src/app/payment-integrations/fis/service/fis.service';
import { PaymentusService } from 'apps/public-portal/src/app/payment-integrations/paymentus/service/paymentus.service';
import { InvoiceCloudService } from 'apps/public-portal/src/app/payment-integrations/invoice-cloud/service/invoice-cloud.service';
import { GovolutionService } from 'apps/public-portal/src/app/payment-integrations/govolution/service/govolution.service';
import { FiservService } from 'apps/public-portal/src/app/payment-integrations/fiserv/service/fiserv.service';
import { LexisNexisService } from 'apps/public-portal/src/app/payment-integrations/lexis-nexis/service/lexis-nexis.service';

/**
 * Single source of truth: Maps payment integration enum to its service class.
 * When adding a new integration, add it here.
 * Uses a lazy getter to avoid circular dependency issues at module load time.
 */
let _paymentProcessorClassMap: Partial<Record<PaymentIntegrationEnumDto, PaymentProcessorClass>> | null = null;

export function getPaymentProcessorClassMap(): Partial<Record<PaymentIntegrationEnumDto, PaymentProcessorClass>> {
    if (!_paymentProcessorClassMap) {
        _paymentProcessorClassMap = {
            [PaymentIntegrationEnumDto.Datacap]: DatacapService,
            [PaymentIntegrationEnumDto.Nic]: NicService,
            [PaymentIntegrationEnumDto.Demo]: DemoService,
            [PaymentIntegrationEnumDto.Celero]: CeleroService,
            [PaymentIntegrationEnumDto.BridgePay]: BridgePayService,
            [PaymentIntegrationEnumDto.Elavon]: ElavonService,
            [PaymentIntegrationEnumDto.PointAndPay]: PointAndPayService,
            [PaymentIntegrationEnumDto.CyberSource]: CyberSourceService,
            [PaymentIntegrationEnumDto.WellsFargo]: WellsFargoService,
            [PaymentIntegrationEnumDto.Fis]: FisService,
            [PaymentIntegrationEnumDto.Paymentus]: PaymentusService,
            [PaymentIntegrationEnumDto.InvoiceCloud]: InvoiceCloudService,
            [PaymentIntegrationEnumDto.Govolution]: GovolutionService,
            [PaymentIntegrationEnumDto.MSB]: MSBService,
            [PaymentIntegrationEnumDto.Fiserv]: FiservService,
            [PaymentIntegrationEnumDto.LexisNexis]: LexisNexisService,
            [PaymentIntegrationEnumDto.Generic]: GenericService
        };
    }
    return _paymentProcessorClassMap;
}

@Injectable({
    providedIn: 'root'
})
export class PaymentProcessorProvider {
    public defaultConfig: PaymentProcessorConfig;

    public configOverrides: { [key: string]: PaymentProcessorConfig } = {};

    /**
     * Aggregated plugin configuration (UI requirements + per-(system, plugin)
     * labels). Populated when the tenant uses the Generic payment integration
     * for at least one payment method; null otherwise.
     */
    public pluginConfig: AggregatedPluginConfigDto = null;

    /** Flat wallet capability list returned by GetConfig — one entry per
     *  (tellerSystemCode, configKey) combination active on this site. */
    public walletCapabilities: WalletCapabilityEntryDto[] = [];

    /**
     * Every payment method permitted by at least one page on this site — a UNION, not the
     * cart intersection checkout uses. For surfaces with no cart context: blocking a user
     * from saving a method that some page accepts would be wrong.
     */
    public siteAvailablePaymentMethods: PaymentMethodTypeEnum[] = [];

    /**
     * Whether the server actually reported the union. An EMPTY union is a real answer - no
     * active page permits anything - and is honoured. An ABSENT one is not an answer at all
     * (an older or partial /payments/config response), and reading it as "nothing is
     * permitted" would empty the profile page and block saving with no message. Callers
     * fall back to the processor rails in that case - the same fail-open judgement checkout
     * makes when its own availability call rejects.
     */
    public siteAvailabilityReported: boolean = false;

    /** If the configuration was loaded correctly with valid configuration. */
    public validConfig: boolean = true;

    public activeCreditProcessor: PaymentProcessorService;

    public activeECheckProcessor: PaymentProcessorService;

    public creditProcessorName: PaymentIntegrationEnumDto;

    public eCheckProcessorName: PaymentIntegrationEnumDto;

    protected _configLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);

    public configLoaded$: Observable<boolean> = this._configLoaded.asObservable();

    constructor(
        private siteMetadataService: TellerOnlineSiteMetadataService,
        private sharedPaymentApi: SharedPaymentIntegrationApiClient,
        private injector: Injector
    ) {
        this.creditProcessorName = this.siteMetadataService.appConfiguration.creditProcessor;
        this.eCheckProcessorName = this.siteMetadataService.appConfiguration.eCheckProcessor;

        // Resolve which processor is being used for each
        this.activeCreditProcessor = this._determinePaymentProcessor(this.creditProcessorName);
        this.activeECheckProcessor = this._determinePaymentProcessor(this.eCheckProcessorName);

        this.setPaymentConfig();
    }

    /**
     * Get parameters that are specific to a payment integration by name and payment method type.
     */
    public getProcessorSpecificConfigParam(paramName: string, paymentMethodType: PaymentMethodTypeEnum) {
        switch (paymentMethodType) {
            case PaymentMethodTypeEnum.CreditCard:
                return this.defaultConfig.creditAdditionalData?.[paramName];
            case PaymentMethodTypeEnum.ECheck:
                return this.defaultConfig.eCheckAdditionalData?.[paramName];
            default:
                return undefined;
        }
    }

    /**
     * Assign properties to the configuration.
     */
    public setPaymentConfig() {
        this.defaultConfig = new PaymentProcessorConfig();

        firstValueFrom(this.sharedPaymentApi.getConfig())
            .then(response => {
                // Assign all top level (processor agnostic) config properties
                this.defaultConfig.setupConfig(response.defaultConfig);

                this.configOverrides = {};
                response.configOverrides.forEach(o => {
                    const configOverride = new PaymentProcessorConfig();
                    configOverride.setupConfig(o);
                    this.configOverrides[o.configKey] = configOverride;
                });

                // TODO (PROD-290): Address the multiple config problem here
                // Assign processor specific config properties (these should also be defined in the service itself).
                // `assignAdditionalProps` can be overridden in the derived services if necessary.
                this.activeCreditProcessor.assignAdditionalProps(this.defaultConfig.creditAdditionalData, PaymentMethodTypeEnum.CreditCard);
                this.activeECheckProcessor.assignAdditionalProps(this.defaultConfig.eCheckAdditionalData, PaymentMethodTypeEnum.ECheck);

                this.activeCreditProcessor.supportsTokenization = response.defaultConfig.creditConfig.supportsTokenization;
                this.activeECheckProcessor.supportsTokenization = response.defaultConfig.eCheckConfig.supportsTokenization;

                this.siteAvailabilityReported = !!response.siteAvailablePaymentMethods;
                this.siteAvailablePaymentMethods = (response.siteAvailablePaymentMethods ?? [])
                        .map(method => PaymentMethodTypeEnumConvertor.fromDto(method));

                this.pluginConfig = response.pluginConfig ?? null;
                this.walletCapabilities = response.walletCapabilities ?? [];

                this._configLoaded.next(true);
            })
            .catch(error => this.handleConfigError(error));
    }

    public useConvenienceFee(paymentMethodType: PaymentMethodTypeEnum) {
        this.defaultConfig.useConvenienceFee(paymentMethodType);
        Object.values(this.configOverrides).forEach(o => o.useConvenienceFee(paymentMethodType));
    }

    /** Get all of the configurations in one object. */
    public get configs() {
        const configs = {};
        configs[DEFAULT_CONFIG_KEY] = this.defaultConfig;

        Object.keys(this.configOverrides).forEach(key => {
            configs[key] = this.configOverrides[key];
        });

        return configs;
    }

    get canSavePaymentMethod(): boolean {
        // Site union rather than the processor flags: a method permitted on any page is
        // legitimate to save, even if the page the user is currently looking at forbids it.
        return (
            // TODO (PROD-276): Re-evaluate if we can no longer assume Credit & E-Check use the same processor.
            (this.isSitePermitted(PaymentMethodTypeEnum.CreditCard)
                && this.activeCreditProcessor.supportsTokenization)
            || (this.isSitePermitted(PaymentMethodTypeEnum.ECheck)
                && this.activeECheckProcessor.supportsTokenization)
        );
    }

    /**
     * Whether the site permits this method anywhere. Falls back to the processor's own rail
     * flag when the union was never reported - see siteAvailabilityReported.
     */
    public isSitePermitted(paymentMethodType: PaymentMethodTypeEnum): boolean {
        if (!this.siteAvailabilityReported) {
            return paymentMethodType === PaymentMethodTypeEnum.CreditCard
                ? this.defaultConfig.isCreditEnabled
                : this.defaultConfig.isECheckEnabled;
        }

        return this.siteAvailablePaymentMethods.includes(paymentMethodType);
    }

    /**
     * Provides the appropriate processor based on payment method type.
     * @param paymentMethodType Payment type of the target processor.
     * @returns The approprirate active processor.
     */
    public getProcessorFromMethodType(paymentMethodType: PaymentMethodTypeEnum): PaymentProcessorService {
        switch (paymentMethodType) {
            case PaymentMethodTypeEnum.CreditCard:
                return this.activeCreditProcessor;
            case PaymentMethodTypeEnum.ECheck:
                return this.activeECheckProcessor;
            default:
                return null;
        }
    }

    /**
     * Get the name of the active processor based on the payment method.
     * @param paymentMethod Payment method to be used.
     * @returns Name of the active processor.
     */
    public getActiveProcessorName(paymentMethod: PaymentMethodData) {
        switch (paymentMethod.type) {
            case PaymentMethodTypeEnum.CreditCard:
                return this.creditProcessorName;
            case PaymentMethodTypeEnum.ECheck:
                return this.eCheckProcessorName;
            default:
                return null;
        }
    }

    public getConvenienceFeeLabel(paymentMethodType?: PaymentMethodTypeEnum, configKey?: string) {
        // Grab the correct configuration, presence of configKey signifies an override.
        const config = configKey ? this.configOverrides[configKey] : this.defaultConfig;

        let label;

        // Grab tender specific label
        switch (paymentMethodType) {
            case PaymentMethodTypeEnum.CreditCard:
                label = config?.creditConvenienceFee?.label;
                break;
            case PaymentMethodTypeEnum.ECheck:
                label = config?.eCheckConvenienceFee?.label;
                break;
            default:
                break;
        }

        // Default label is always "Convenience Fee"
        return label ?? 'Convenience Fee';
    }

    public getTestTriggerData(paymentMethodType: PaymentMethodTypeEnum) {
        switch (paymentMethodType) {
            case PaymentMethodTypeEnum.CreditCard:
                return this.defaultConfig.creditTestTriggerData;
            case PaymentMethodTypeEnum.ECheck:
                return this.defaultConfig.eCheckTestTriggerData;
            default:
                return null;
        }
    }

    /**
     * The payment methods permitted for a cart. The whole availability rule — processor
     * capability, page and connection policy, and fee-inclusive max amounts, intersected
     * across the cart's partitions — runs on the server; this only transports the answer.
     * Do not reimplement any part of that decision on the client.
     */
    public async getAvailablePaymentMethods(
        cartGuid: string
    ): Promise<AvailablePaymentMethodsResponseDto> {
        return firstValueFrom(
            this.sharedPaymentApi.getAvailablePaymentMethods(cartGuid));
    }

    /**
     * Calculate the conveninence fee.
     * @param cartGuid Identitifier for the target cart.
     * @param paymentMethodType Optionally specify payment method type. If not specified,
     *                          use the type set from `processorConfig.useConvenienceFee().`
     * @returns
     */
    public async calculateConvenienceFee(cartGuid: string, paymentMethodType?: PaymentMethodTypeEnum): Promise<ConvenienceFeeResponseDto> {
        paymentMethodType ??= this.defaultConfig.usingConvenienceFeeType ?? PaymentMethodTypeEnum.Undetermined;

        return this.sharedPaymentApi.calculateConvenienceFee(cartGuid, PaymentMethodTypeEnumConvertor.toDto(paymentMethodType)).toPromise();
    }

    /**
     * Resolve which payment processor service is to be used and return it.
     * Uses PaymentProcessorClassMap as the single source of truth.
     * @param processor Enum valued with the paymentIntegration to be used
     * @returns The corresponding processor service instance
     */
    private _determinePaymentProcessor(processor: PaymentIntegrationEnumDto): PaymentProcessorService {
        const processorClass = getPaymentProcessorClassMap()[processor];

        if (!processorClass) {
            // TODO: How big of an issue is it if we don't have an active processor? Should we break the app (throw an error, etc)?
            console.error('No active payment processor.');
            return null;
        }

        return this.injector.get(processorClass);
    }

    /**
     * To be used to generate an error callback for subscription for `getConfig`.
     * Note: This needs to be invoked, and the function returned is the callback.
     * @returns Error callback with reference to the payment service (`this`) via closure.
     */
    private handleConfigError(error: IErrorDto) {
        this.validConfig = false; // This flag disables checkout.

        if (error?.errorDef === 'ConfigurationRequired') {
            error.errorMessage = 'An internal error occurred. Checkout has been disabled.';
        }

        // Wrap in Error so prefer-throw-literal stays happy while keeping the
        // structured fields downstream consumers still inspect on the thrown
        // value (errorDef / errorMessage).
        throw Object.assign(new Error(error?.errorMessage ?? 'Config error'), error);
    }

    /**
     * Allows the currently active payment processor (by payment method type)
     * to perform some cleanup steps before leaving the checkout page
     * If the payment method type comes in as undetermined, it means there was no active processor at the time of deactivate
     * @param paymentMethodType Which payment method was selected when we tried to deactivate
     */
    public async deactivateCheckout(paymentMethodType: PaymentMethodTypeEnum) {
        if (paymentMethodType === PaymentMethodTypeEnum.CreditCard) {
            await this.activeCreditProcessor.deactivateCheckout();
        } else if (paymentMethodType === PaymentMethodTypeEnum.ECheck) {
            await this.activeECheckProcessor.deactivateCheckout();
        }
    }
}
